import { AsgardCloudBaseService } from '../asgard-cloud-base.service';
import { Injectable } from '@angular/core';
import { CloudServiceEnum, MetadataTypesEnum, SaveStatusEnum } from '../enums';
import { CloudFoldersEnum } from 'src/app/utils/enums/cloud-folders.enum';
import { CloudData } from '../metadata/CloudData';
import { DriveMetadata } from '../metadata/DriveMetadata';
import { OneDriveItemMetadata, OneDriveApiService, OneDriveItemParent } from './one-drive.api.service';
import { TabService } from '../../tab.service';
import { FileConvertorService } from '../../file-convertor/file-convertor-service';
import { ModalWindowService } from '../../ui/modal-window.service';
import { DocumentManagerService } from '../../document/document-manager.service';
import { ToastrDialogService } from '../../toastr-dialog.service';
import { BlobToPdfService } from '../../blob-to-pdf.service';
import { TranslateService } from '@ngx-translate/core';
// import { CommonMessageDialogComponent } from 'src/app/components/dialogs/common-message-dialog/common-message-dialog.component';
import { isK3000domain, parseExtenstion } from 'src/app/core/common';
import { FileCopyInfo } from '../models/file-copy-info';
import { EventTypeEnum, MimeTypesEnum, ToastrActionEnum, ToastrStringsEnum } from 'src/app/utils/enums';
import { FileData } from 'src/models/FileData';
import { SettingsService } from '../../settings/settings.service';
import { ISettings } from 'src/app/core/interfaces';
import { ExtraCopyDialogComponent } from 'src/app/components/dialogs/extra-copy-dialog/extra-copy-dialog.component';
import { CloudException, CloudExceptionEnum } from '../models/exceptions';
import { getMimeTypeEnumFromExt, getMimeTypeEnumFromString, SupportedExtensions, validateMimetypes } from '../common';
import { EventBus } from '../../auth/event.bus';
import { CloudRecentFilesService } from '../cloud-recent-files.service';
import { ExamModeRestrictionsService } from '../../exam/exam-mode-restrictions.service';
import { MatDialog } from '@angular/material/dialog';
import { AccountService } from '../../auth/account.service';
import { LicenseService } from '../../license.service';
import { OpenFileService } from '../../open-file.service';
import { AsgardDocEpub } from 'src/app/core/documents/asgard-doc-epub';
import { EpubFileService } from '../../epub/epub-file.service';

interface OneDriveFileUploadModel {
  file: File;
  parentId: string;
  driveId: string;
  fileName: string;
  isUpdate: boolean;
  fileId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AsgardCloudOneDriveService extends AsgardCloudBaseService {
  private file: File;
  private uploadInfo: OneDriveFileUploadModel;
  private currentCloudFolder: CloudFoldersEnum;

  private metadataList: OneDriveItemMetadata[] = [];

  private get isSharedFolder(): boolean {
    return this.currentCloudFolder === CloudFoldersEnum.Shared;
  }

  constructor(
    tabService: TabService,
    convertorService: FileConvertorService,
    modalService: ModalWindowService,
    docMgrService: DocumentManagerService,
    toastrService: ToastrDialogService,
    blobToPdfService: BlobToPdfService,
    epubFileService: EpubFileService,
    translateService: TranslateService,
    examModeService: ExamModeRestrictionsService,
    dialog: MatDialog,
    accountService: AccountService,
    licenseService: LicenseService,
    openFileService: OpenFileService,
    private readonly oneDriveApi: OneDriveApiService,
    private readonly settings: SettingsService,
    private readonly eventBus: EventBus,
    private readonly cloudRecentFilesService: CloudRecentFilesService,
    private readonly translate: TranslateService,
  ) {
    super(
      tabService, convertorService, modalService, docMgrService,
      toastrService, blobToPdfService, epubFileService, translateService, examModeService,
      dialog, accountService, licenseService, openFileService
    );
    this.initSubscriptions();
  }

  async getFiles(currentFolder: string, showAllFiles: boolean, dialogStatus: SaveStatusEnum,
    currentCloudFolder: CloudFoldersEnum): Promise<CloudData[]> {
    let files: OneDriveItemMetadata[] = [];
    let cloudDataList: CloudData[] = [];

    try {
      if (currentCloudFolder === CloudFoldersEnum.Shared) {
        if (currentFolder === 'root') {
          files = await this.oneDriveApi.getSharedFiles();
        } else {
          const driveId = this.getRemoteDriveId(currentFolder);
          files = await this.oneDriveApi.getFiles(currentFolder, driveId);
        }
      } else if (currentCloudFolder === CloudFoldersEnum.Recent) {
        files = await this.cloudRecentFilesService.getFiles(CloudServiceEnum.OneDrive) as OneDriveItemMetadata[];
      } else if (currentFolder === 'root') {
        files = await this.oneDriveApi.getRootFiles();
      } else {
        files = await this.oneDriveApi.getFiles(currentFolder);
      }
      
      // NOTE 12 nov 2025
      // for now, hide the Personal Vault folder as it requires special handling
      // it does not have a unique something
      // the name is also dependent on the language of the OneDrive account
      // BUT it does not have a file or folder property
      files = files.filter((x) => x.file || x.folder);

      this.metadataList = [];
      files = !files ? [] : files;

      files.forEach((item: OneDriveItemMetadata) => {
        const cloudData = this.mapToCloudData(item);
        item.type = MetadataTypesEnum.OneDriveMetadata;
        cloudDataList.push(cloudData);
        this.metadataList.push(item);
      });

      if (!showAllFiles || dialogStatus === SaveStatusEnum.Ocr) {
        cloudDataList = this.filterUnsupportedFiles(cloudDataList, dialogStatus);
      }

      return cloudDataList;
    } catch (_err) {
      // this.displayError(new CloudException(CloudExceptionEnum.fileRetrieval));
      throw _err;
    }
  }

  async newFolder(name: string, cloudData: CloudData | null): Promise<void> {
    await this.oneDriveApi.createFolder((cloudData === null ? this.ROOT_FOLDER : cloudData.id), name.trim(), cloudData?.parents || undefined).catch((error) => {
      console.error(error);
      if (error.status === 403) {
        this.displayError(new CloudException(error.status));
      } else {
        this.displayGeneralError();
      }
    });
  }

  async copyOrMoveFile(fileCopyInfo: FileCopyInfo): Promise<void> {
    const {
      fileToCopy,
      filename,
      copy: isCopy,
      parentToCopy,
    } = fileCopyInfo;

    try {
      if (fileToCopy) {
        const fileMetadata = await this.oneDriveApi.getFileMetadata(fileToCopy.id, fileToCopy.parents);
        const { id: fileId, parentReference: { driveId, id: parentId } } = fileMetadata;
        let parentMetadata: OneDriveItemMetadata;

        if (parentToCopy) {
          parentMetadata = await this.oneDriveApi.getFileMetadata(parentToCopy.id, parentToCopy.parents);
        } else {
          parentMetadata = await this.oneDriveApi.getRootMetadata();
        }

        const parentInfo: OneDriveItemParent = {
          id: parentMetadata.id,
          driveId: parentMetadata.parentReference.driveId,
        };

        if (isCopy) {
          await this.oneDriveApi.copyFile(fileId, parentInfo, filename, driveId);
        } else {
          this.checkFileMovingPath(parentId, parentInfo.id);
          await this.oneDriveApi.moveFile(fileId, parentInfo, filename, driveId);
        }
      }
    } catch (err) {
      // let messageKey = `CLOUDERRORS.FILE_${isCopy ? 'COPY' : 'MOVE'}`;

      // if (err instanceof CloudException) {
      //   messageKey = err.toString();
      // }

      // this.modalService.createModalWindow(CommonMessageDialogComponent, { messageKey });
      this.displayError(new CloudException(isCopy ? CloudExceptionEnum.fileCopy : CloudExceptionEnum.fileMove));
    }
  }

  async deleteFile(file: CloudData): Promise<void> {
    try {
      return await this.oneDriveApi.deleteFile(file.id, file.parents);
    } catch (err) {
      this.displayError(new CloudException(CloudExceptionEnum.fileDelete));
    }
  }

  async updateFile(file: CloudData, params: Partial<DriveMetadata>): Promise<void> {
    try {
      if (params.name) {
        const metadata: Partial<OneDriveItemMetadata> = {
          name: params.name
        };

        this.cloudRecentFilesService.removeFileFromRecent(file.id, CloudServiceEnum.OneDrive);
        const updatedFileMeta = await this.oneDriveApi.updateFileMetadata(file.id, metadata);
        this.cloudRecentFilesService.addFileToRecent(updatedFileMeta, CloudServiceEnum.OneDrive);
      }
    } catch (err) {
      this.displayError(new CloudException(CloudExceptionEnum.fileUpload));
      // this.modalService.createModalWindow(CommonMessageDialogComponent, { messageKey: 'COMMONMESSAGEDIALOG.FILE_UPDATE_ERROR' });
    }
  }

  async getFileFromCloud(fileId: string, dialogStatus: SaveStatusEnum, paneIndex: number): Promise<void> {
    const fileMetadata = this.getFileMetadata(fileId, this.isSharedFolder);
    if (!fileMetadata) {
      return;
    }

    this.getFile(fileId, dialogStatus)
      .then((file) => {
        this.file = file;
        this.processFiles(this.file, dialogStatus, fileMetadata, paneIndex);
        this.cloudRecentFilesService.addFileToRecent(fileMetadata, CloudServiceEnum.OneDrive);
      })
      .catch((error) => {
        console.error(error);
        if (error !== 'different user') {
          this.displayGeneralError();
        }
      });
  }

  saveAudiofile(audiofile: File, currentFolder: CloudData): void {
    this.uploadToCloud(currentFolder, audiofile.name, audiofile);
  }

  async uploadToCloud(parent: CloudData | null, filename: string | null | undefined, file: File | null = null): Promise<void> {
    try {
      if (!file || file.type !== MimeTypesEnum.Mp3) {
        if (this.tabService.activeTab && (filename === undefined || filename === '')) {
          this.oneDriveMetadata = this.tabService.activeTab.oneDriveMetadata;
          filename = this.tabService.activeTab.title;
        }
        if (this.tabService.activeTab && filename && (filename !== this.tabService.activeTab.title)) {
          this.tabService.activeTab.setTabTitle(filename);
        }

        if (!file) {
          if (this.docMgrService.currentDoc instanceof AsgardDocEpub) {
            file = await this.epubFileService.exportWithAnnotations();
          } else {
            file = await this.blobToPdfService.getCurrentPDF();
          }

          // getCurrentPDF() can return null. if it does, do not continue.
          if (!file) {
            this.displayGeneralError();
            return;
          }
        }
      }

      let parentId: string;
      let driveId: string | null;

      if (!!!parent) {
        const rootFolder = await this.oneDriveApi.getRootMetadata();

        parentId = rootFolder.id;
        driveId = rootFolder.parentReference.driveId;

        // If audiomaker && destination is root, relay to or create audiomaker folder
        if (file && file.type === MimeTypesEnum.Mp3) {
          parentId = await this.createOrAddAudiomakerFolder(parentId, driveId);
        }
      } else {
        parentId = parent.id;
        driveId = parent.parents;
      }

      if (file && filename) {
        const fileData: FileData = this.checkFilenameBeforeUpload(file, filename, parentId);
        this.upload(fileData, driveId);
      }
    } catch (err) {
      this.displayError(new CloudException(CloudExceptionEnum.fileUpload));
    }
  }

  async createOrAddAudiomakerFolder(parent: string = 'root', driveId: string): Promise<string> {
    const foldername = isK3000domain() ? this.K3000_AUDIOMAKER_FOLDER : this.ALINEA_AUDIOMAKER_FOLDER;

    // Check for existing folder and create one if necessary
    const rootContent: OneDriveItemMetadata[] = await this.oneDriveApi.getRootFiles();

    let parentId;
    rootContent.forEach(item => {
      if (item.name === foldername) {
        parentId = item.id;
      }
    });

    if (parentId) {
      return parentId;
    } else {
      const result: OneDriveItemMetadata = await this.oneDriveApi.createFolder(parent, foldername, driveId);
      return result.id;
    }
  }

  async upload(fileData: FileData, driveId: string): Promise<void> {
    const parentId = fileData.path;
    const file = fileData.file;

    this.uploadInfo = {
      file,
      parentId,
      driveId,
      fileName: fileData.filename,
      isUpdate: !this.getSaveAs(),
      fileId: this.oneDriveMetadata?.id || undefined,
    };

    if (file.type === MimeTypesEnum.Mp3) {
      this.uploadFile();
      return;
    } else if (this.getSaveAs()) {
      for (const fileMeta of this.metadataList) {
        if ((fileMeta.name === fileData.filename || fileMeta.name + '.pdf' === fileData.filename) &&
          (fileMeta.parentReference.id.indexOf(fileData.path) > -1 || fileData.path === 'root')) {
          this.modalService.createModalWindow(ExtraCopyDialogComponent);
          return;
        }
      }
    }

    this.uploadFile();
  }

  async uploadFile(): Promise<void> {
    const toastrKey = this.getTranslatedTitle();
    this.toastrService.showToastrByType(ToastrActionEnum.Info, ToastrStringsEnum.Uploading, true, toastrKey);

    const { parentId, driveId, fileName, file, isUpdate, fileId } = this.uploadInfo;

    try {
      if (isUpdate && fileId) {
        this.cloudRecentFilesService.removeFileFromRecent(fileId, CloudServiceEnum.OneDrive);
      }

      const uploadedFileId = await this.oneDriveApi.uploadFile(parentId, fileName, file, isUpdate, driveId);

      if (uploadedFileId) {
        const uploadedFileMetadata = await this.oneDriveApi.getFileMetadata(uploadedFileId, driveId);
        this.cloudRecentFilesService.addFileToRecent(uploadedFileMetadata, CloudServiceEnum.OneDrive);
        uploadedFileMetadata.type = MetadataTypesEnum.OneDriveMetadata;
        this.fillMetadata(uploadedFileMetadata);
        this.toastrService.showToastrByType(ToastrActionEnum.Success, ToastrStringsEnum.UploadDone, false, toastrKey);
      }

      if (this.tabService.activeTab && this.tabService.activeTab.isOriginallyKES && this.uploadInfo.file.type !== MimeTypesEnum.Mp3) {
        // this.modalService.createModalWindow(CommonMessageDialogComponent, { messageKey: 'COMMONMESSAGEDIALOG.PDF_KES_SAVE_SUCCESS' });
        this.toastrService.showToaster(ToastrActionEnum.Success, this.translateService.instant('COMMONMESSAGEDIALOG.PDF_KES_SAVE_SUCCESS'));
      }
    } catch (err) {
      this.toastrService.showToastrByType(ToastrActionEnum.Error, ToastrStringsEnum.CloudError, false, toastrKey);
      if (err && (err.status === 403 || err.status === 507)) {
        this.displayError(new CloudException(err.status));
      } else {
        this.displayError(new CloudException(CloudExceptionEnum.fileUpload));
      }
    }

  }

  checkIfFileIsTrashedOrGone(): void {
    throw new Error('Not Implemented');
  }

  getFileFromId(fileId: string): Promise<File> {
    return this.getFile(fileId, SaveStatusEnum.ExamModeFilePicker)
      .then((file) => {
        this.toastrService.hideToastr(file.name);
        return Promise.resolve(file);
      })
      .catch((error) => {
        console.error(error);
        this.displayGeneralError();
        const metadata = this.getFileMetadata(fileId, this.isSharedFolder);
        this.toastrService.hideToastr(metadata.name);
        return Promise.reject();
      });
  }

  private mapToCloudData(item: OneDriveItemMetadata): CloudData {
    const itemData = item.remoteItem ? item.remoteItem : item;

    const mimeType = itemData.folder ? MimeTypesEnum.Folder :
      itemData.file && this.getMimetype(item) as MimeTypesEnum;

    return {
      id: itemData.id,
      isShared: !!itemData.shared,
      name: itemData.name ?? item.name ?? '', // NOTE 07/11/2015 Personal Vault does not have a name in item.remoteItem
      size: itemData.size, // && itemData.size.toString(),
      parents: itemData.parentReference.driveId,
      path: itemData.parentReference.id,
      mimeType: mimeType,
      modifiedTime: itemData.lastModifiedDateTime || item.lastModifiedDateTime,
      extension: mimeType !== MimeTypesEnum.Folder && itemData.name ? parseExtenstion(itemData.name) : '',
    };
  }

  private getFileMetadata(fileId: string, shared: boolean): OneDriveItemMetadata {
    let result: OneDriveItemMetadata | undefined;

    if (shared) {
      result = this.metadataList
        .find(x => (x.remoteItem ? x.remoteItem.id : x.id) === fileId);
    } else {
      result = this.metadataList.find(x => x.id === fileId);
    }
    if (!result) {
      result = this.metadataList[0];
    }

    return result;
  }

  private getRemoteDriveId(fileId: string): string {
    const metadata = this.getFileMetadata(fileId, true);

    if (metadata) {
      const parentRef = metadata.remoteItem ? metadata.remoteItem.parentReference : metadata.parentReference;
      return parentRef.driveId;
    }

    const index = fileId.indexOf('!');
    return fileId.substring(0, index);
  }

  private getMimetype(metadata: OneDriveItemMetadata): MimeTypesEnum {
    const itemData = metadata.remoteItem ? metadata.remoteItem : metadata;
    const fileExt = itemData.name && itemData.name.lastIndexOf('.') >= 0 && itemData.name.lastIndexOf('.') < itemData.name.length - 1 ?
      itemData.name.substr(itemData.name.lastIndexOf('.') + 1) : '';
    const cloudMimetype = getMimeTypeEnumFromString(itemData.file.mimeType);

    return fileExt && SupportedExtensions.includes(fileExt) ? getMimeTypeEnumFromExt(itemData.name) : cloudMimetype;
  }

  private initSubscriptions(): void {
    this.settings.getSettingsObservable().subscribe((e: ISettings) => {
      this.currentCloudFolder = e.cloudSettings.cloudFolder;
    })

    this.eventBus.on(EventTypeEnum.ONE_DRIVE_ERROR).subscribe(e => {
      const toastrKey = this.getTranslatedTitle();
      this.toastrService.hideToastr(toastrKey);

      if (e && e.data && e.data.status === 403) {
        this.displayError(new CloudException(e.data.status));
      } else {
        this.displayError(e.data.status);
      }
    })
  }

  private validateFileSupport(metadata: OneDriveItemMetadata, dialogStatus: SaveStatusEnum): boolean {
    const mimeType = getMimeTypeEnumFromString(this.getMimetype(metadata));
    const vMetadata = { fileName: metadata.name, mimeType };
    const isOcr = dialogStatus === SaveStatusEnum.Ocr;
    return validateMimetypes(vMetadata, isOcr, true);
  }

  private displayGeneralError(): void {
    this.displayError(new CloudException(CloudExceptionEnum.fileRetrieval));
  }

  private async getFile(fileId: string, dialogStatus: SaveStatusEnum): Promise<File> {
    let fileMetadata = this.getFileMetadata(fileId, this.isSharedFolder);

    if (!fileMetadata) {
      return Promise.reject();
    }

    if (!this.validateFileSupport(fileMetadata, dialogStatus)) {
      // this.modalService.createModalWindow(CommonMessageDialogComponent, { messageKey: 'COMMONMESSAGEDIALOG.NOT_SUPPORTED' });
      this.toastrService.showToaster(ToastrActionEnum.Error, this.translate.instant('COMMONMESSAGEDIALOG.NOT_SUPPORTED'));
      return Promise.reject();
    }

    try {
      if (this.isSharedFolder && !fileMetadata['@microsoft.graph.downloadUrl']) {
        fileMetadata = await this.oneDriveApi.getFileMetadata(fileMetadata.id, fileMetadata.remoteItem.parentReference.driveId);
      }

      const userId = this.accountService.userId;

      const dataFile = await this.oneDriveApi.downloadFile(fileMetadata['@microsoft.graph.downloadUrl']);

      // if it is still the same user that is logged in
      const currentUserId = this.accountService.userId;
      if (userId !== currentUserId) {
        return Promise.reject('different user');
      }

      if (dataFile) {
        let fileExtension = getMimeTypeEnumFromString(this.getMimetype(fileMetadata));

        if (fileMetadata.name.indexOf('.rtf') > -1) {
          fileExtension = MimeTypesEnum.ApplicationRtf;
        }

        if (fileExtension) {
          const file = this.createFileFromBlob(dataFile, fileExtension, fileMetadata.name);
          return Promise.resolve(file);
        }
      }
      return Promise.reject();
    } catch (error) {
      return Promise.reject(error);
    }
  }
}
